import React, {forwardRef} from 'react'
import {Button as ChakraButton, ButtonProps as ChakraButtonProps} from '@chakra-ui/react'

interface ButtonProps extends Omit<ChakraButtonProps, 'variant' | 'size' | 'width' | 'onClick' | 'type'> {
    as?: React.ElementType
    to?: string | React.ReactNode
    type?: 'button' | 'submit' | 'reset'
    variant?:
        | 'primary'
        | 'secondary'
        | 'newsletter'
        | 'changelocale'
        | 'filter'
        | 'textLink'
        | 'discoverMore'
        | 'outline'
        | 'outlineLight'
        | 'text'
        | 'textUnderline'
        | 'underline'
        | 'round-swatch'
    size?: 'xs' | 'sm' | 'md' | 'newsletter' | 'changelocale'
    width?: 'xs' | 'sm' | 'md' | 'lg' | 'none'
    isDisabled?: boolean
    children: React.ReactNode
    onClick?: (e?) => void
    href?: string
    enableWrap?: boolean
}

/**
 * @name Button
 *
 * @component
 *
 * @description
 * Renders a Chakra UI button with customizable properties.
 *
 * @param {ButtonProps} props - The properties object.
 * @param {React.ElementType} props.as - The element or component to render as the button.
 * @param {string} [props.type='submit'] - The type of the button.
 * @param {string} [props.variant='primary'] - The variant of the button.
 * @param {string} [props.size='sm'] - The size of the button.
 * @param {string} [props.width='sm'] - The width of the button.
 * @param {boolean} [props.isDisabled=false] - Whether the button is disabled.
 * @param {function} props.onClick - The click event handler for the button.
 * @param {boolean} [props.enableWrap=false] - Indicates if to enable wrap behaviour for the label inside the button.
 * @param {React.ReactNode} props.children - The content to be displayed inside the button.
 * @param {React.Ref<HTMLButtonElement>} ref - A ref to the underlying button element.
 *
 * @returns {JSX.Element} The rendered button component.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({as, type = 'submit', variant = 'primary', size = 'sm', width = 'none', isDisabled = false, onClick, children, href, enableWrap, ...props}, ref) => {
        let customWidth, customSize

        if (variant === 'primary' || variant === 'secondary' || variant === 'outline' || variant === 'newsletter' || variant === 'filter') {
            customWidth = `size.button.${width}`
        }

        if (variant === 'textLink') {
            customSize = `textLink-${size}`
        }

        if (variant === 'discoverMore') {
            customSize = `discoverMore`
        }

        return (
            <ChakraButton
                as={as}
                type={type}
                variant={variant}
                size={customSize ? customSize : size}
                maxWidth={customWidth}
                isDisabled={isDisabled}
                onClick={onClick}
                href={href}
                whiteSpace={enableWrap ? 'normal' : 'nowrap'}
                wordwrap={enableWrap ? 'break-word' : 'normal'}
                py={enableWrap ? 'spacing.3' : 'auto'}
                h={enableWrap ? 'fit-content' : variant === 'round-swatch' ? undefined : 'auto'}
                ref={ref}
                {...props}
            >
                {children}
            </ChakraButton>
        )
    },
)

Button.displayName = 'Button'

export {Button, ButtonProps}
